import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, ClipboardList } from 'lucide-react';
import { atlasApi, AtlasUser, AvaliacaoFormulario, AvaliacaoCompetencia, AvaliacaoCompetenciaTipo } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import ListaEditavel, { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const TIPO_LABEL: Record<AvaliacaoCompetenciaTipo, string> = {
  nota_1_5: 'Nota 1-5', texto: 'Texto', escolha: 'Escolha',
};

const toCsv = (arr?: string[]) => (arr || []).join(', ');
const fromCsv = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

function novaCompetencia(): AvaliacaoCompetencia {
  return { id: `c${Date.now()}${Math.floor(Math.random() * 1000)}`, nome: '', descricao: '', peso: 1, tipo: 'nota_1_5', opcoes: [] };
}

interface FormState {
  nome: string;
  competencias: (AvaliacaoCompetencia & { _opcoesCsv?: string })[];
}

const vazio = (): FormState => ({ nome: '', competencias: [{ ...novaCompetencia(), _opcoesCsv: '' }] });

const Formularios: React.FC<Props> = ({ user, podeEditar }) => {
  const [itens, setItens] = useState<AvaliacaoFormulario[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(vazio());
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getFormularios(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => { setEditId(null); setForm(vazio()); setFb(null); setModalAberto(true); };

  const abrirEdicao = (f: AvaliacaoFormulario) => {
    setEditId(f._id);
    setForm({
      nome: f.nome || '',
      competencias: (f.competencias && f.competencias.length ? f.competencias : [novaCompetencia()])
        .map(c => ({ ...c, _opcoesCsv: toCsv(c.opcoes) })),
    });
    setFb(null);
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setFb({ tipo: 'erro', msg: 'Nome é obrigatório.' }); return; }
    const competencias: AvaliacaoCompetencia[] = form.competencias
      .filter(c => c.nome.trim())
      .map(c => {
        const base: AvaliacaoCompetencia = {
          id: c.id, nome: c.nome.trim(), peso: Number(c.peso) || 1, tipo: c.tipo,
        };
        if (c.descricao && c.descricao.trim()) base.descricao = c.descricao.trim();
        if (c.tipo === 'escolha') base.opcoes = fromCsv(c._opcoesCsv || '');
        return base;
      });
    if (competencias.length === 0) { setFb({ tipo: 'erro', msg: 'Adicione ao menos uma competência com nome.' }); return; }
    setSalvando(true);
    setFb(null);
    const body = { nome: form.nome.trim(), competencias };
    try {
      if (editId) await atlasApi.editarFormulario(user, editId, body);
      else await atlasApi.criarFormulario(user, body);
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (f: AvaliacaoFormulario) => {
    if (!window.confirm(`Excluir o formulário "${f.nome}"?`)) return;
    try {
      await atlasApi.excluirFormulario(user, f._id);
      carregar();
    } catch (e: any) {
      setErro(e.message || 'Erro ao excluir.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex-1">Formulários de avaliação</h2>
        {podeEditar && (
          <button onClick={abrirNovo} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Novo formulário
          </button>
        )}
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : itens.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhum formulário cadastrado.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {itens.map(f => (
            <div key={f._id} className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0"><ClipboardList className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900">{f.nome}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{(f.competencias || []).length} competência{(f.competencias || []).length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              {podeEditar && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                  <button onClick={() => abrirEdicao(f)} className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => excluir(f)} className="text-xs text-rose-600 hover:underline flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Excluir
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalAberto(false)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 flex-1">{editId ? 'Editar formulário' : 'Novo formulário'}</h3>
              <button onClick={() => setModalAberto(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className={labelCls}>Nome *</label>
                <input className={inputCls} value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Avaliação de competências comerciais" />
              </div>

              <div className="pt-2">
                <label className={labelCls}>Competências</label>
                <ListaEditavel<FormState['competencias'][number]>
                  itens={form.competencias}
                  onChange={competencias => setForm(p => ({ ...p, competencias }))}
                  novoItem={() => ({ ...novaCompetencia(), _opcoesCsv: '' })}
                  rotuloAdd="adicionar competência"
                  vazioMsg="Nenhuma competência."
                  renderLinha={(c, atualizar) => (
                    <div className="space-y-2">
                      <input className={inputCls} placeholder="Nome da competência" value={c.nome} onChange={e => atualizar({ nome: e.target.value })} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <select className={inputCls} value={c.tipo} onChange={e => atualizar({ tipo: e.target.value as AvaliacaoCompetenciaTipo })}>
                          {(Object.keys(TIPO_LABEL) as AvaliacaoCompetenciaTipo[]).map(k => <option key={k} value={k}>{TIPO_LABEL[k]}</option>)}
                        </select>
                        <input className={inputCls} type="number" min={0} step={0.5} placeholder="Peso" value={c.peso} onChange={e => atualizar({ peso: Number(e.target.value) })} />
                      </div>
                      <input className={inputCls} placeholder="Descrição (opcional)" value={c.descricao || ''} onChange={e => atualizar({ descricao: e.target.value })} />
                      {c.tipo === 'escolha' && (
                        <input className={inputCls} placeholder="Opções (separadas por vírgula)" value={c._opcoesCsv || ''} onChange={e => atualizar({ _opcoesCsv: e.target.value })} />
                      )}
                    </div>
                  )}
                />
              </div>

              <FeedbackBanner feedback={fb} />

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModalAberto(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Formularios;
